import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { GlassCard } from '../../../../../components/glass';
import type { IContaCilHomologacao } from '../../../../../hooks/useContaCilDashboard';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const Barra = ({
  label,
  valor,
  pct,
  gradient,
  delay,
}: {
  label: string;
  valor: number;
  pct: number;
  gradient: string;
  delay: number;
}) => (
  <Box>
    <Flex justify="space-between" align="baseline" mb={1}>
      <Text fontSize="12px" fontWeight="600" color="#0F172A">
        {label}
      </Text>
      <Text fontSize="11px" color="#64748B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(valor)} · {pct.toFixed(1).replace('.', ',')}%
      </Text>
    </Flex>
    <Box position="relative" h="26px" borderRadius="8px" bg="#EEF0F6" overflow="hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(pct, 1.5)}%` }}
        transition={{ duration: 0.55, delay, ease: [0.4, 0, 0.2, 1] }}
        style={{ position: 'absolute', inset: 0, borderRadius: '8px', background: gradient }}
      />
    </Box>
  </Box>
);

const Chip = ({ texto, cor }: { texto: string; cor: string }) => (
  <Flex align="center" gap={1.5} bg={`${cor}14`} px={2.5} py={1} borderRadius="full">
    <Box w="6px" h="6px" borderRadius="full" bg={cor} />
    <Text fontSize="11px" color={cor} fontWeight="600">
      {texto}
    </Text>
  </Flex>
);

// Homologação de PDFs (extração + validação). Só extratos em PDF exigem isso —
// OFX/XLSX já vêm estruturados.
export const HomologacaoPdf = ({ data }: { data?: IContaCilHomologacao }) => {
  const pdf = data?.pdf_importados ?? 0;
  const extraidos = data?.extraidos ?? 0;
  const homologados = data?.homologados ?? 0;
  const base = Math.max(1, pdf);
  const naoExtraidos = Math.max(0, pdf - extraidos);
  const naoHomologados = Math.max(0, extraidos - homologados);

  return (
    <GlassCard intensity="medium" p={6} h="full">
      <Flex justify="space-between" align="flex-start" gap={3} mb={4}>
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            Homologação de extratos
          </Text>
          <Text fontSize="15px" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            Extração e homologação (PDF)
          </Text>
        </Box>
        <Text
          fontSize="10px"
          fontWeight="700"
          color="#7C3AED"
          bg="rgba(124,58,237,0.1)"
          px={2}
          py={1}
          borderRadius="full"
          textTransform="uppercase"
          letterSpacing="0.04em"
          flexShrink={0}
        >
          somente PDF
        </Text>
      </Flex>

      <Text fontSize="12px" color="#64748B" mb={4}>
        De <b style={{ color: '#0F172A' }}>{formatNumber(pdf)}</b> extratos em PDF importados no
        período.
      </Text>

      <Flex direction="column" gap={4}>
        <Barra
          label="Extraídos"
          valor={extraidos}
          pct={(extraidos / base) * 100}
          gradient="linear-gradient(90deg, #7c5cff, #a78bfa)"
          delay={0}
        />
        <Barra
          label="Homologados"
          valor={homologados}
          pct={(homologados / base) * 100}
          gradient="linear-gradient(90deg, #10b981, #1fa971)"
          delay={0.08}
        />
      </Flex>

      <Flex gap={2} mt={4} wrap="wrap">
        <Chip texto={`${formatNumber(naoExtraidos)} não extraídos`} cor="#7C3AED" />
        <Chip texto={`${formatNumber(naoHomologados)} não homologados`} cor="#3B7DDD" />
      </Flex>
    </GlassCard>
  );
};
